import { anmeldekopf, basisUrl, rufe } from './client';

/**
 * Der Zugang zur Belegausgabe (ANG-12, ANG-13, AUF-08).
 *
 * ZWEI WEGE ZUM BELEG, und der Unterschied ist wesentlich:
 *
 *   `vorschau`     holt HTML. Sie braucht den Setzdienst NICHT — wer den
 *                  Briefkopf ändert, sieht das Ergebnis auch dann, wenn der
 *                  Renderer gerade nicht läuft.
 *   `erzeugen`     setzt das PDF, legt es ab und vermerkt es (AUF-08). Das
 *                  ist der Beleg, der aus dem Haus geht.
 */

export type Belegart = 'angebot' | 'auftrag' | 'rechnung';

/**
 * RNG-13: in welcher Gestalt der Beleg ausgegeben wurde.
 *
 * `pdf` und `zugferd` sind **nicht** dasselbe. Ein PDF ohne eingebettetes XML
 * ist seit dem 01.01.2025 keine E-Rechnung mehr, sondern ein Bild von einer
 * Rechnung. `xrechnung` ist das reine XML, ohne PDF darum herum.
 */
export type Belegformat = 'pdf' | 'html' | 'zugferd' | 'xrechnung';

/** Wie die Formate in der Maske heißen. */
export const FORMATNAME: Record<Belegformat, string> = {
  pdf: 'PDF',
  html: 'HTML',
  zugferd: 'ZUGFeRD (PDF mit eingebettetem XML)',
  xrechnung: 'XRechnung (nur XML)',
};

/** RNG-15: in welchem Format eine Rechnung hinausgeht — und warum. */
export type Ausgabeformatauskunft = {
  readonly format: Belegformat;
  /**
   * `kunde` heißt: am Empfänger ist etwas hinterlegt. `voreinstellung` heißt:
   * niemand hat sich damit befasst. „PDF" bedeutet in beiden Fällen dasselbe
   * Dokument und etwas ganz anderes für die Frage, ob noch etwas zu tun ist.
   */
  readonly herkunft: 'kunde' | 'voreinstellung';
  readonly empfaenger: string;
  /** Ein Widerspruch, der auffallen soll — etwa PDF trotz Leitweg-ID. */
  readonly hinweis: string | null;
};

/** RNG-14: das letzte Prüfergebnis gegen das KoSIT-Regelwerk. */
export type Pruefstand = {
  readonly regelwerk: string;
  readonly gueltig: boolean;
  readonly befunde: readonly {
    readonly schwere: string;
    readonly regel: string;
    readonly feld: string;
    readonly meldung: string;
  }[];
  readonly geprueftAm: string;
};

export async function ausgabeformat(rechnung: string): Promise<Ausgabeformatauskunft> {
  return rufe(`/belegausgabe/rechnung/${rechnung}/ausgabeformat`);
}

export async function pruefbericht(ausgabe: string): Promise<Pruefstand | null> {
  return rufe(`/belegausgabe/ausgaben/${ausgabe}/pruefung`);
}

export type Platzhalter = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly beispiel: string;
  readonly istListe: boolean;
};

export type Vorlagenzeile = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly belegart: Belegart;
  readonly kundenart: string | null;
  readonly aktiv: boolean;
  readonly abgeloest: boolean;
  readonly angelegtAm: string;
};

export type Ausgabe = {
  readonly id: string;
  readonly belegart: Belegart;
  readonly beleg: string;
  readonly vorlage: string;
  readonly vorlagenkennung: string;
  readonly vorlagenversion: number;
  readonly format: Belegformat;
  readonly dateiname: string;
  readonly ablageschluessel: string;
  readonly pruefsumme: string;
  readonly groesseBytes: number;
  readonly erzeugtVonName: string | null;
  readonly erzeugtAm: string;
  /** ANG-12: Wann und von wem in der Vorschau geprüft und freigegeben. */
  readonly geprueftAm: string | null;
  readonly geprueftVonName: string | null;
  readonly versendetAm: string | null;
  readonly empfaenger: string | null;
  readonly versandergebnis: string | null;
};

export async function vorlagen(belegart?: Belegart): Promise<readonly Vorlagenzeile[]> {
  return rufe(`/belegausgabe/vorlagen${belegart === undefined ? '' : `?belegart=${belegart}`}`);
}

export async function platzhalter(belegart: Belegart): Promise<readonly Platzhalter[]> {
  return rufe(`/belegausgabe/platzhalter/${belegart}`);
}

export type Vorlage = Vorlagenzeile & {
  readonly rumpfHtml: string;
  readonly kopfHtml: string | null;
  readonly fussHtml: string | null;
  readonly stilCss: string | null;
  readonly seitenformat: 'A4' | 'A5' | 'Letter';
  readonly randObenMm: string;
  readonly randUntenMm: string;
  readonly randLinksMm: string;
  readonly randRechtsMm: string;
  readonly bemerkung: string | null;
  readonly bearbeiterName: string | null;
  readonly stand: string;
  readonly fassungen: readonly {
    readonly id: string;
    readonly version: number;
    readonly abgeloest: boolean;
    readonly angelegtAm: string;
  }[];
  readonly unbekanntePlatzhalter: readonly string[];
};

export async function vorlage(id: string): Promise<Vorlage> {
  return rufe(`/belegausgabe/vorlagen/${id}`);
}

/**
 * AUF-08: Eine Änderung erzeugt eine neue FASSUNG.
 *
 * Nicht PATCH: Es entsteht eine Zeile, keine wird geändert. Ein bereits
 * erzeugter Beleg zeigt auf die alte, und die bleibt lesbar.
 */
export async function neueFassung(
  id: string,
  felder: {
    readonly bezeichnung?: string;
    readonly rumpfHtml?: string;
    readonly kopfHtml?: string | null;
    readonly fussHtml?: string | null;
    readonly stilCss?: string | null;
    readonly seitenformat?: 'A4' | 'A5' | 'Letter';
    readonly randObenMm?: number;
    readonly randUntenMm?: number;
    readonly randLinksMm?: number;
    readonly randRechtsMm?: number;
    readonly aktiv?: boolean;
    readonly bemerkung?: string | null;
  },
): Promise<{ readonly id: string; readonly version: number }> {
  return rufe(`/belegausgabe/vorlagen/${id}/fassungen`, { verfahren: 'POST', rumpf: felder });
}

/**
 * Die Vorschau eines ENTWURFS — mit Beispieldaten, ohne Beleg.
 *
 * Wer gestaltet, hat noch kein Angebot vor sich; und gespeichert wird eine
 * neue Fassung (AUF-08) — man hätte nach einem Nachmittag Gestaltung dreißig
 * davon.
 */
export async function entwurfsvorschau(entwurf: {
  readonly belegart: Belegart;
  readonly rumpfHtml: string;
  readonly kopfHtml: string | null;
  readonly fussHtml: string | null;
  readonly stilCss: string | null;
}): Promise<{
  readonly html: string;
  readonly unbekannt: readonly string[];
  /** PRF-17 (CRM-169): Pflichtabschnitte der Belegart, die fehlen — etwa {{#anrechnungen}}. */
  readonly fehlendeAbschnitte: readonly string[];
}> {
  return rufe('/belegausgabe/vorschau', { verfahren: 'POST', rumpf: entwurf });
}

export async function ausgaben(belegart: Belegart, beleg: string): Promise<readonly Ausgabe[]> {
  return rufe(`/belegausgabe/${belegart}/${beleg}/ausgaben`);
}

/**
 * Die Vorschau als HTML-Text.
 *
 * ALS TEXT UND NICHT ALS URL FÜR EIN `<iframe src>`. Der Grund ist der
 * Schlüssel: Ein `<iframe>` kann keinen Anmeldekopf setzen, und die API
 * arbeitet mit einem Bearer-Schlüssel und nicht mit einem Cookie. Der Text
 * wird deshalb hier geholt und in einen `srcdoc`-Rahmen gehängt.
 *
 * Der Rahmen ist kein Beiwerk: Der Beleg trägt CSS aus der Vorlage, und ohne
 * eigenen Kontext liefe es in die Maske hinein. Die Belegdaten maskiert der
 * Server (vorlagensatz.ts); die Gestaltung bleibt Gestaltung und gehört in
 * ihren eigenen Rahmen.
 */
export async function vorschau(
  belegart: Belegart,
  beleg: string,
  vorlage?: string,
): Promise<string> {
  const frage = vorlage === undefined ? '' : `?vorlage=${vorlage}`;
  const antwort = await fetch(
    `${basisUrl()}/belegausgabe/${belegart}/${beleg}/vorschau${frage}`,
    { headers: anmeldekopf() },
  );
  if (!antwort.ok) {
    let rumpf: unknown;
    try { rumpf = await antwort.json(); } catch { rumpf = {}; }
    throw Object.assign(new Error('Vorschau fehlgeschlagen'), {
      status: antwort.status,
      ...(rumpf as object),
    });
  }
  return antwort.text();
}

/**
 * Erzeugt das PDF und gibt es als Datenblock zurück.
 *
 * NICHT über `rufe`: Die Antwort ist kein JSON, sondern das Dokument selbst.
 */
export async function erzeugen(
  belegart: Belegart,
  beleg: string,
  vorlage?: string,
): Promise<{ readonly blob: Blob; readonly dateiname: string }> {
  const frage = vorlage === undefined ? '' : `?vorlage=${vorlage}`;
  const antwort = await fetch(
    `${basisUrl()}/belegausgabe/${belegart}/${beleg}/erzeugen${frage}`,
    { method: 'POST', headers: anmeldekopf() },
  );

  if (!antwort.ok) {
    /*
     * Der Fehler kommt als JSON zurück und wird HIER in dieselbe Form
     * gebracht wie bei `rufe` — sonst zeigte die Maske für diesen einen Weg
     * eine andere Fehlerdarstellung als für alle anderen (ERR-01).
     */
    let rumpf: unknown;
    try {
      rumpf = await antwort.json();
    } catch {
      rumpf = { kennung: 'unbekannt', meldung: { wasGeschah: 'Der Beleg konnte nicht erzeugt werden.' } };
    }
    throw Object.assign(new Error('Belegausgabe fehlgeschlagen'), {
      status: antwort.status,
      ...(rumpf as object),
    });
  }

  return { blob: await antwort.blob(), dateiname: dateinameAus(antwort) };
}

/** Der Dateiname aus dem `content-disposition` — mit Rückfallwert. */
function dateinameAus(antwort: Response): string {
  const kopf = antwort.headers.get('content-disposition') ?? '';
  return /filename="([^"]+)"/.exec(kopf)?.[1] ?? 'beleg.pdf';
}

/** Ein bereits erzeugtes Dokument, geprüft gegen seine Prüfsumme (DAT-05). */
export async function datei(ausgabe: string): Promise<Blob> {
  const antwort = await fetch(`${basisUrl()}/belegausgabe/ausgaben/${ausgabe}/datei`, {
    headers: anmeldekopf(),
  });
  if (!antwort.ok) throw new Error('Das Dokument konnte nicht geladen werden.');
  return antwort.blob();
}

/**
 * SUP-19: aus welchem Postfach dieser Beleg gehen wird — samt Begründung.
 *
 * `aktiv` und `versandbereit` sind zwei Aussagen: das eine die fachliche
 * Freigabe des Postfachs, das andere, ob auch Zugangsdaten hinterlegt sind.
 * Die Maske unterscheidet sie, weil die Abhilfe eine andere ist.
 */
export type Versandauskunft =
  | {
      readonly gefunden: false;
      readonly anlass: Belegart;
      readonly kundenart: 'privat' | 'geschaeft' | 'oeffentlich' | null;
    }
  | {
      readonly gefunden: true;
      readonly anlass: Belegart;
      readonly kundenart: 'privat' | 'geschaeft' | 'oeffentlich' | null;
      readonly kennung: string;
      readonly bezeichnung: string;
      readonly absender: string;
      readonly absenderName: string;
      readonly regel: string;
      readonly aktiv: boolean;
      readonly versandbereit: boolean;
    };

/**
 * ANG-12: die Freigabe nach der letzten Sicht in der Vorschau.
 *
 * Ein eigener Aufruf und kein Haken am Versandformular: Ein Haken, den man im
 * selben Atemzug mit dem Absenden setzt, wird mitgeklickt. Die Freigabe nennt
 * eine Person und ist danach nicht mehr änderbar.
 */
export async function freigeben(ausgabe: string): Promise<Ausgabe> {
  return rufe(`/belegausgabe/ausgaben/${ausgabe}/geprueft`, { verfahren: 'POST' });
}

export async function versandauskunft(ausgabe: string): Promise<Versandauskunft> {
  return rufe(`/belegausgabe/ausgaben/${ausgabe}/versandauskunft`);
}

/**
 * ANG-14, AUF-09, RNG-18: den erzeugten Beleg versenden.
 *
 * Er geht in die **Outbox**, nicht an den Mailserver — der Aufruf kehrt sofort
 * zurück. Ein Mailserver, der nicht antwortet, hält die Maske nicht an
 * (ERR-08). `neu: false` heißt: Dieser Beleg war schon eingestellt; es
 * entsteht keine zweite Nachricht.
 */
export async function versenden(
  ausgabe: string,
  eingabe: {
    readonly empfaenger: readonly string[];
    readonly betreff: string;
    readonly text: string;
  },
): Promise<{ readonly id: string; readonly neu: boolean }> {
  return rufe(`/belegausgabe/ausgaben/${ausgabe}/versenden`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}
